import React, { ReactNode, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import useOnClickOutside from '@vtblife/uikit/dist/hooks/use-on-click-outside';

interface Props {
    onClose?: () => void;
    className: string;
    children: ReactNode;
    dataTest?: string;
    banClosing?: boolean;
}

export const ChatAside: (props: Props) => React.ReactPortal = ({
    onClose,
    className,
    children,
    banClosing = false,
    dataTest,
}) => {
    const asideRef = useRef<HTMLDivElement>(null);
    useSimpleBodyScrollLock(true);
    useOnClickOutside([asideRef], onClose!, banClosing);

    return createPortal(
        <aside className={className} ref={asideRef} data-test={dataTest}>
            {children}
        </aside>,
        document.body,
    );
};

// This exists because useLockBodyScroll from react-use packages
// miserably fails on iOS devices. It prevents touchmoves on the documents level,
// so any scrolling modal will fail to scroll it's inner content. See:

// Code - https://github.com/streamich/react-use/blob/master/src/useLockBodyScroll.ts#L60
// Issue with modals - https://github.com/streamich/react-use/issues/1477
// Ohter issues - https://github.com/streamich/react-use/issues?q=is%3Aissue+is%3Aopen+useLockBodyScroll
const doc: Document | undefined = typeof document === 'object' ? document : undefined;
const scrollingElem = doc ? ((doc.scrollingElement || doc.body) as HTMLElement) : undefined;

function useSimpleBodyScrollLock(enabled: boolean) {
    const defaultOverflow = useMemo(() => scrollingElem?.style.overflow || '', []);
    const defaultScrollTop = useMemo(() => scrollingElem?.scrollTop || 0, []);

    useEffect(() => {
        if (enabled && scrollingElem) {
            scrollingElem.style.overflow = 'hidden';
        }

        return () => {
            if (enabled && scrollingElem) {
                scrollingElem.style.overflow = defaultOverflow;
                scrollingElem.scrollTop = defaultScrollTop;
            }
        };
    }, [enabled, defaultOverflow, defaultScrollTop]);
}
